/**
 * OnboardingDrawerComponent — checklist complète de démarrage vendeur.
 *
 * Ouvert via BusinessOnboardingService.openDrawer() depuis n'importe où
 * (FAB, hero, lien navbar, etc.). Affiche :
 *   - Bandeau de progression global + récap (X/Y étapes)
 *   - Liste des étapes groupées par phase (Compte / Boutique / Catalogue /
 *     Opérations / Visibilité / Succès)
 *   - Chaque étape : icône, titre, description, statut, summary, CTA,
 *     bouton "Plus tard" (dismiss 24h).
 *
 * Le composant est purement présentationnel : il lit le state via le
 * service et délègue toutes les actions au service.
 */

import { CommonModule } from '@angular/common';
import { Component, computed, inject } from '@angular/core';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';

import { VdDrawerComponent } from '../../ui/drawer/drawer.component';
import { VdButtonComponent } from '../../ui/button/button.component';
import {
  BusinessOnboardingService,
  BusinessOnboardingStep,
  OnboardingPhase,
  OnboardingStatus,
} from '../../services/business-onboarding.service';

interface PhaseGroup {
  key: OnboardingPhase;
  /** Clé i18n du libellé de phase. */
  labelKey: string;
  emoji: string;
  /** Clé i18n de la description de phase. */
  descKey: string;
  steps: BusinessOnboardingStep[];
  done: number;
  total: number;
}

const PHASE_META: Record<
  OnboardingPhase,
  { labelKey: string; emoji: string; descKey: string }
> = {
  account: {
    labelKey: 'onboarding.phase.account',
    emoji: '👤',
    descKey: 'onboarding.phase.account_desc',
  },
  shop: {
    labelKey: 'onboarding.phase.shop',
    emoji: '🏪',
    descKey: 'onboarding.phase.shop_desc',
  },
  catalog: {
    labelKey: 'onboarding.phase.catalog',
    emoji: '📦',
    descKey: 'onboarding.phase.catalog_desc',
  },
  ops: {
    labelKey: 'onboarding.phase.ops',
    emoji: '🚚',
    descKey: 'onboarding.phase.ops_desc',
  },
  reach: {
    labelKey: 'onboarding.phase.reach',
    emoji: '📣',
    descKey: 'onboarding.phase.reach_desc',
  },
  success: {
    labelKey: 'onboarding.phase.success',
    emoji: '🎉',
    descKey: 'onboarding.phase.success_desc',
  },
};

const PHASE_ORDER: OnboardingPhase[] = [
  'account',
  'shop',
  'catalog',
  'ops',
  'reach',
  'success',
];

/**
 * Étapes dont les libellés sont traduits CÔTÉ FRONT via les clés
 * ``onboarding.steps.<key>.*`` (fr/en/ar-TN). Les étapes historiques
 * gardent les textes FR renvoyés par le backend — on n'interroge pas
 * transloco pour elles afin de ne pas spammer /i18n/missing.
 */
const I18N_STEP_KEYS = new Set<string>(['migrate_tiktak']);

@Component({
  selector: 'app-onboarding-drawer',
  standalone: true,
  imports: [CommonModule, TranslocoModule, VdDrawerComponent, VdButtonComponent],
  templateUrl: './onboarding-drawer.component.html',
  styleUrls: ['./onboarding-drawer.component.css'],
})
export class OnboardingDrawerComponent {
  private readonly svc = inject(BusinessOnboardingService);
  private readonly i18n = inject(TranslocoService);

  /** Helper pour usages dans les attributs (template-friendly). */
  t(key: string, params?: Record<string, unknown>): string {
    return this.i18n.translate(key, params);
  }

  readonly state = this.svc.state;
  readonly loading = this.svc.loading;
  readonly progress = this.svc.progress;
  readonly remainingCount = this.svc.remainingCount;
  readonly isComplete = this.svc.isComplete;

  readonly groups = computed<PhaseGroup[]>(() => {
    const s = this.state();
    if (!s) return [];
    const byPhase = new Map<OnboardingPhase, BusinessOnboardingStep[]>();
    for (const step of s.steps) {
      const arr = byPhase.get(step.phase) ?? [];
      arr.push(step);
      byPhase.set(step.phase, arr);
    }
    return PHASE_ORDER
      .filter((k) => byPhase.has(k))
      .map<PhaseGroup>((k) => {
        const steps = byPhase.get(k)!;
        return {
          key: k,
          labelKey: PHASE_META[k].labelKey,
          emoji: PHASE_META[k].emoji,
          descKey: PHASE_META[k].descKey,
          steps,
          done: steps.filter((s) => s.status === 'done').length,
          total: steps.length,
        };
      });
  });

  readonly congratsVisible = computed<boolean>(() => this.isComplete());

  close(): void {
    this.svc.closeDrawer();
  }

  startStep(step: BusinessOnboardingStep): void {
    this.svc.startStep(step);
  }

  dismiss(step: BusinessOnboardingStep, ev: Event): void {
    ev.stopPropagation();
    this.svc.dismissReminder(step.key);
  }

  refresh(): void {
    this.svc.refresh();
  }

  // ─── Helpers de présentation ─────────────────────────────────────

  /** Traduction i18n d'un champ d'étape avec fallback sur le texte backend. */
  private stepText(step: BusinessOnboardingStep, field: string, fallback: string): string {
    if (!I18N_STEP_KEYS.has(step.key)) return fallback;
    const key = `onboarding.steps.${step.key}.${field}`;
    const translated = this.i18n.translate(key);
    return translated && translated !== key ? translated : fallback;
  }

  stepTitle(step: BusinessOnboardingStep): string {
    return this.stepText(step, 'title', step.title);
  }

  stepDescription(step: BusinessOnboardingStep): string {
    return this.stepText(step, 'description', step.description);
  }

  stepSummary(step: BusinessOnboardingStep): string {
    // Toujours privilégier le summary dynamique backend (counts réels :
    // « 1 catégorie détectée ») — les clés i18n summary_todo/done sont
    // trop statiques et masquent la détection réelle.
    if (step.summary) return step.summary;
    const variant = step.status === 'done' ? 'summary_done' : 'summary_todo';
    return this.stepText(step, variant, step.summary);
  }

  stepCta(step: BusinessOnboardingStep): string {
    if (step.cta_label) return step.cta_label;
    const variant = step.status === 'done' ? 'cta_done' : 'cta_todo';
    return this.stepText(step, variant, step.cta_label);
  }

  statusEmoji(s: OnboardingStatus): string {
    if (s === 'done') return '✅';
    if (s === 'partial') return '🟡';
    if (s === 'blocked') return '⛔';
    return '◯';
  }

  statusLabel(s: OnboardingStatus): string {
    return this.i18n.translate(`onboarding.status.${s}`);
  }

  statusTone(s: OnboardingStatus): string {
    if (s === 'done') return 'done';
    if (s === 'partial') return 'partial';
    if (s === 'blocked') return 'blocked';
    return 'todo';
  }

  trackByPhase = (_: number, g: PhaseGroup) => g.key;
  trackByStep = (_: number, s: BusinessOnboardingStep) => s.key;
}
